import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Button, Divider, Flex, Grid, AttentionMessage, Typography } from '@tq/ui-components';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { tqc } from '@tq/ui-components/dist/system';
import { useFormatLocaleDate } from 'config/DateFormats/useFormatLocaleDate';

import { dfiReportIsEditingState, annualDfiReportYearState } from 'store';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { PageHeading } from 'components';
import { KpiAccordion, CompletionModal, Review } from './components';
import { useFormattedDfiReport } from './hooks';
import { DfiReportResponse } from 'types';

interface DraftDfiReportProps {
  heading: string;
  report: DfiReportResponse;
}

export const DraftDfiReport: React.FC<DraftDfiReportProps> = ({ heading, report }) => {
  const isMobile = useIsMobile();

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { t } = useTranslation('annual_dfi_report_draft');

  const isEditing = useRecoilValue(dfiReportIsEditingState);
  const reportYear = useRecoilValue(annualDfiReportYearState);

  const { titles, metrics: kpiData } = useFormattedDfiReport(report);

  const handleClose = () => setModalOpen(false);
  const formatLocaleDate = useFormatLocaleDate();

  return (
    <>
      <Grid gridGap={4}>
        <PageHeading heading={heading} />
        <AttentionMessage
          status='info'
          width={isMobile ? 'unset' : 'max-content'}
          title={`${t('report_submission_info', 'Report must be submitted by 1st July')} ${
            reportYear + 1
          }`}
          variant='inline'
        />
      </Grid>
      {report && <KpiAccordion kpiData={kpiData} titles={titles} />}
      <Divider my={8} borderColor={COLOURS.gray[300]} />
      <Review />
      <Divider my={8} borderColor={COLOURS.gray[300]} />
      <Flex
        justifyContent={
          report.dateModified && report.consulteeName ? 'space-between' : 'flex-end'
        }
      >
        {report.dateModified && report.consulteeName && (
          <Typography color={COLOURS.gray[800]}>
            <tqc.p fontSize={'0.875rem !important'}>
              {`${t('last_edited', 'Last edited')} ${formatLocaleDate(
                report.dateModified
              )} ${t('by', 'By').toLowerCase()} ${report.consulteeName}`}
            </tqc.p>
          </Typography>
        )}
        <Button disabled={isEditing} onClick={() => setModalOpen(true)}>
          {t('complete', 'Complete')}
        </Button>
      </Flex>
      <CompletionModal
        isOpen={modalOpen}
        onClose={handleClose}
        report={report}
        heading={heading}
      />
    </>
  );
};
